import { Image, useTheme, View } from "native-base";
import React from "react";
import Svg, { Path } from "react-native-svg";
import { useSelector } from "react-redux";

import { Scale, VerticalScale, width as deviceWidth } from "../constants";
import { shouldWeChangeAppToHormoneTheme } from "../constants/theme";
import { FeatureFlag, isFeatureFlagActive } from "../helpers/featureFlags";
import { getOrganisation } from "../helpers/userHelpers";
import type { Organisation } from "../services/backendTypes";
import { onboardingDataSelector } from "../slices/onboardingSlice";
import { userSelector } from "../slices/userSlice";

const LogoCurve = ({
  color,
  width,
}: {
  color: string | undefined;
  width: number | string | undefined;
}): JSX.Element => (
  <Svg testID={"customBrandingMobileCurve-svg"} width={width} height={VerticalScale(125)} fill="none">
    <Path
      d="M445.002 0C441.825 4.84658 438.222 9.40029 434.236 13.6069C432.174 15.639 430.159 17.7461 427.952 19.6339L426.329 21.0887C425.786 21.5736 425.249 22.0585 424.678 22.5088L421.293 25.2568C420.179 26.1978 418.983 27.0233 417.828 27.9008C416.673 28.7783 415.518 29.6327 414.299 30.4698L410.684 32.9059L408.87 34.1241L407.016 35.2787L403.308 37.5879C402.072 38.3499 400.796 39.0426 399.537 39.7757C398.278 40.5089 397.019 41.2363 395.742 41.929L391.885 43.9669C371.214 54.6816 349.186 62.5618 326.754 68.6118C323.959 69.4085 321.135 70.0954 318.316 70.8055L314.089 71.8677L309.844 72.8433L305.593 73.8133C304.178 74.1308 302.746 74.3905 301.325 74.6964L297.057 75.5682L294.92 76.0011L292.777 76.3938C281.365 78.576 269.872 80.3194 258.344 81.7454L254.018 82.288L249.686 82.7383L245.355 83.177C243.911 83.3329 242.467 83.4714 241.018 83.5811L232.354 84.3027L223.691 84.88C212.1 85.5497 200.491 85.9827 188.876 86.0346C177.262 86.0866 165.653 85.7344 154.05 85.1225C142.447 84.5105 130.873 83.5522 119.334 82.236C107.795 80.9197 96.2957 79.1821 84.866 77.1154L80.5748 76.3418L76.3009 75.4932L71.9636 74.6157C70.537 74.3328 69.122 74.0037 67.7013 73.6919C62.024 72.4508 56.3813 71.0596 50.756 69.6048C39.517 66.6548 28.3645 63.3296 17.4315 59.3635C6.48162 55.4378 -4.2307 50.879 -14.6512 45.7104L-18.5324 43.7303C-19.8261 43.0664 -21.0851 42.3389 -22.3673 41.6462C-23.6494 40.9534 -24.92 40.2607 -26.1733 39.5275L-29.9274 37.2992L-31.7986 36.185L-33.6352 35.0304L-37.3027 32.6693C-38.5155 31.8726 -39.6937 31.0182 -40.8835 30.1869C-42.0732 29.3556 -43.2745 28.5359 -44.4123 27.6295L-47.8776 24.9681C-48.4551 24.5351 -49.0326 24.0676 -49.5698 23.5942L-51.2389 22.1913C-53.4971 20.367 -55.5763 18.335 -57.7074 16.3722C-61.8368 12.3079 -65.623 7.90928 -69.0273 3.22136C-65.3247 7.6341 -61.283 11.7511 -56.9393 15.535C-54.7099 17.3535 -52.5615 19.2586 -50.2282 20.9328L-48.4955 22.2317C-47.918 22.6647 -47.3404 23.0976 -46.7629 23.496L-43.2167 25.9322C-33.6444 32.2544 -23.5599 37.7657 -13.0688 42.4082C-2.61536 47.1223 8.09818 51.2375 19.0198 54.7336C29.9354 58.2551 40.982 61.334 52.1595 63.9704C74.5229 69.1476 97.1931 72.8979 120.033 75.1988C131.451 76.3533 142.892 77.2655 154.356 77.837C165.821 78.4086 177.302 78.6049 188.778 78.5702C211.74 78.5678 234.685 77.2977 257.507 74.7658C280.329 72.3259 302.969 68.4035 325.282 63.0235C336.448 60.3294 347.461 57.1505 358.323 53.4865C369.203 49.871 379.86 45.6167 390.238 40.7457L394.12 38.904C395.413 38.2805 396.672 37.5994 397.949 36.9528C399.225 36.3062 400.513 35.6655 401.761 34.9669L405.509 32.8655L407.386 31.8149L409.222 30.7006L412.89 28.455C414.109 27.6987 415.275 26.8732 416.482 26.0938C417.689 25.3145 418.891 24.5409 420.034 23.675L423.499 21.1463C424.077 20.7365 424.655 20.2863 425.232 19.836L426.907 18.4909C429.217 16.759 431.273 14.7731 433.439 12.8795C437.652 8.92199 441.52 4.61324 445.002 0Z"
      fill={color}
      stroke={color}
    />
  </Svg>
);

type Props = { organisationForPreview?: Organisation | undefined; previewingOnDesktopScreen?: boolean };
const CustomBrandingMobileScreenHeader = (props: Props): JSX.Element => {
  const { colors } = useTheme();

  const user = useSelector(userSelector);
  const onboardingData = useSelector(onboardingDataSelector);

  if (!user) {
    return <></>;
  }

  const organisation = props?.organisationForPreview || getOrganisation(user);

  if (!organisation || !organisation.logo_visible) return <></>;

  const isHormoneFeatureFlagActive = isFeatureFlagActive(user, FeatureFlag.FF_FEMALE_HORMONES);
  const isKillerBodyOrganisation = organisation.subdomain === "killerbody";
  const shouldUseHormoneLogo =
    isHormoneFeatureFlagActive && shouldWeChangeAppToHormoneTheme(user, onboardingData) && isKillerBodyOrganisation;
  const logoSource = shouldUseHormoneLogo
    ? // TODO: We should replace this require
      // eslint-disable-next-line global-require
      require("../resources/killerbody_hormone_logo.png")
    : { uri: organisation.logo };

  return (
    <View alignItems="center" mt="15" mb={5} position={"relative"} testID={"customBrandingMobile-header"} zIndex={999}>
      {organisation.logo || shouldUseHormoneLogo ? (
        <Image
          source={logoSource}
          style={{ width: Scale(160), height: VerticalScale(60) }}
          resizeMode="contain"
          alt="Coach logo"
        />
      ) : (
        <View style={{ width: Scale(160), height: VerticalScale(60) }}></View>
      )}
      {!props?.previewingOnDesktopScreen ? (
        <View style={{ position: "absolute", top: -20 }}>
          {!organisation.disable_logo_curve && (
            <LogoCurve
              width={deviceWidth}
              // NOTE: We must use the theme colour because it will be (slightly) different than the organisation colour
              // (because we created a palette from it for the theme colours)
              color={colors.primary["600"]}
            />
          )}
        </View>
      ) : null}
    </View>
  );
};
export default CustomBrandingMobileScreenHeader;
